import { memo, useCallback, useMemo } from 'react'
import type { KeyboardEvent, MouseEvent, ReactElement, SyntheticEvent } from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import Sheet from 'components/utils/Sheet'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { didClickInteractableElement } from 'components/Common/NestableClickable/NestableClickable'
import { CurrencyProvider } from 'contexts/currencyContext'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import focusStyle from 'styles/utils/focusStyle'
import { last } from 'lib/array/arrayUtils'
import { getArrivalDayOffset, getTotalStopCount } from 'lib/flights/flightUtils'
import FlightSummaryRow, { flightCardDesktop, flightCardHeading6 } from './FlightSummaryRow'

interface Props {
  journey: App.JourneyV2
  onSelect?: (data: { journey: App.JourneyV2 }) => void
  onViewDetails?: () => void
  isAlreadyAdded?: boolean
  isSelectDisabled?: boolean
  selectLabel?: string
  selectIcon?: ReactElement
  showSelectButton?: boolean
  showPrice?: boolean
}

const CardQueryContainer = styled.div`
  container-name: chat-flight-card;
  container-type: inline-size;
  width: 100%;
`

const CardShell = styled(Sheet)<{ $interactive: boolean }>`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(12)};
  width: 100%;

  ${flightCardDesktop} {
    align-items: center;
    column-gap: ${rem(12)};
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: ${rem(16)} ${rem(12)};
    row-gap: ${rem(16)};
  }

  @container chat-flight-card (min-width: ${rem(720)}) {
    column-gap: ${rem(24)};
  }

  ${props => props.$interactive && css`
    cursor: pointer;

    &:focus-visible {
      ${focusStyle}
    }

    ${mediaHoverable} {
      &&:hover {
        border-color: ${props.theme.palette.neutral.default.four};
      }
    }
  `}
`

const CardFooter = styled.div`
  align-items: center;
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  box-sizing: border-box;
  display: flex;
  gap: ${rem(12)};
  justify-content: flex-end;
  margin-inline: ${rem(-12)};
  padding: ${rem(12)} ${rem(12)} 0;
  width: calc(100% + ${rem(24)});

  ${flightCardDesktop} {
    border-top: 0;
    margin-inline: 0;
    margin-inline-start: auto;
    padding: 0;
    width: auto;
  }

  @container chat-flight-card (min-width: ${rem(720)}) {
    gap: ${rem(24)};
  }
`

const PriceCell = styled.div`
  min-width: 0;

  /* Figma butts the sale unit straight onto the fare; LuxKit's price row adds 0.25em. */
  && > * {
    column-gap: 0;
  }

  /* See flightCardHeading6 — the price point renders its own LuxKit heading6. */
  && .variant-heading6 {
    ${flightCardHeading6}
  }
`

/** Figma renders the fare in the card's body colour rather than LuxKit's stronger `normal-contrast`. */
const StandardPriceCell = styled(PriceCell)`
  && .variant-heading6 {
    color: ${props => props.theme.palette.neutral.default.two};
  }
`

const ActionCell = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`

function getFirstStopAirport(flights: Array<App.FlightV2>): string {
  return flights.length > 1 ?
    flights[0].arrivalAirport :
    flights[0]?.hiddenStops?.[0]?.airportCode ?? ''
}

function getLayoverDetail(numberOfStops: number, flights: Array<App.FlightV2>): string {
  if (numberOfStops === 0) return ''
  const layoverAirport = getFirstStopAirport(flights)
  if (!layoverAirport) return ''
  if (flights.length < 2) return layoverAirport
  const { hours, minutes } = flights[0].stopoverTime
  if (hours || minutes) {
    return `${hours}h ${minutes}m ${layoverAirport}`
  }
  return layoverAirport
}

function DrawerFlightCard({
  journey,
  onSelect,
  onViewDetails,
  isAlreadyAdded = false,
  isSelectDisabled = false,
  selectLabel: selectLabelOverride,
  selectIcon,
  showSelectButton = true,
  showPrice = true,
}: Props) {
  const intl = useIntl()

  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = last(flights)
  const duration = journey.flightGroup.totalFlightDuration
  const numberOfStops = getTotalStopCount(flights)

  const logoSrc = journey.carrierBrandMark ?? journey.carrierLogo
  const routeLabel = `${firstFlight.departureAirport}-${lastFlight.arrivalAirport}`

  const durationLabel = `${duration.hours}h ${duration.minutes}m`

  const arrivalDaysAhead = getArrivalDayOffset(firstFlight.departingDate, lastFlight.arrivalDate)

  const layoverDetail = useMemo(
    () => getLayoverDetail(numberOfStops, flights),
    [numberOfStops, flights],
  )

  const defaultSelectLabel = intl.formatMessage({
    id: 'FOREyFIFyoG8zaEUykey',
    defaultMessage: 'Add to trip',
    description: 'Button label to add a flight to the trip planner itinerary from the drawer search results',
  })

  const addedLabel = intl.formatMessage({
    id: 'yjcRGlZFznW-to4yKCRx',
    defaultMessage: 'Already in trip',
    description: 'Disabled button label shown when a flight is already in the trip planner itinerary',
  })
  const viewDetailsLabel = intl.formatMessage(
    {
      id: 'efrN4MevEqwji_FiE7VN',
      defaultMessage: 'View flight details for {carrier}, {departureTime} to {arrivalTime}',
      description: 'Accessible label for the trip planner flight result card, which opens the flight details view when activated',
    },
    {
      carrier: journey.carrierName,
      departureTime: firstFlight.departingTime,
      arrivalTime: lastFlight.arrivalTime,
    },
  )

  const handleClick = useCallback(() => {
    if (!onSelect) return
    onSelect({ journey })
  }, [onSelect, journey])

  // Figma has no "Flight details" link on the card, so the card itself opens the
  // details view. Clicks that land on the CTA (or any other nested control) are
  // left to that control.
  const isInteractiveTarget = useCallback((event: SyntheticEvent) =>
    event.target instanceof Element &&
    didClickInteractableElement(event.target as HTMLElement, event.currentTarget as HTMLElement),
  [])

  const handleCardClick = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!onViewDetails || isInteractiveTarget(event)) return
    onViewDetails()
  }, [onViewDetails, isInteractiveTarget])

  const handleCardKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (!onViewDetails || isInteractiveTarget(event)) return
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      onViewDetails()
    }
  }, [onViewDetails, isInteractiveTarget])

  const memberPrice = journey.price.adult.totalFare ?? 0
  const regularPrice = journey.regularPrice?.adult?.totalFare
  const showLuxPlusPrice = journey.bookingInfo.isLuxPlusFare === true && Number.isFinite(regularPrice)

  const priceDetails = showPrice ? (
    <CurrencyProvider currency={journey.currencyCode}>
      {showLuxPlusPrice ?
          <PriceCell>
            <PriceRowLuxPlusPrice size="S" memberPrice={memberPrice} nonMemberPrice={regularPrice} saleUnit="adult" />
          </PriceCell> :
          <StandardPriceCell>
            <PriceRowPrice size="S" price={memberPrice} saleUnit="adult" />
          </StandardPriceCell>}
    </CurrencyProvider>
  ) : null

  const selectButton = showSelectButton ? (
    <AssistChip
      variant="filled"
      size="small"
      startIcon={isAlreadyAdded ? <LineCheckIcon /> : (selectIcon ?? <LinePlusIcon />)}
      onClick={handleClick}
      disabled={isAlreadyAdded || isSelectDisabled}
    >
      {isAlreadyAdded ? addedLabel : (selectLabelOverride ?? defaultSelectLabel)}
    </AssistChip>
  ) : null

  const actionCell = selectButton ? <ActionCell>{selectButton}</ActionCell> : null

  return (
    <CardQueryContainer>
      <CardShell
        background="normal-background"
        border="light-foreground"
        borderRadius="L"
        $interactive={!!onViewDetails}
        role={onViewDetails ? 'button' : undefined}
        tabIndex={onViewDetails ? 0 : undefined}
        aria-label={onViewDetails ? viewDetailsLabel : undefined}
        onClick={onViewDetails ? handleCardClick : undefined}
        onKeyDown={onViewDetails ? handleCardKeyDown : undefined}
      >
        <FlightSummaryRow
          carrierName={journey.carrierName}
          logoSrc={logoSrc}
          departureTime={firstFlight.departingTime}
          arrivalTime={lastFlight.arrivalTime}
          arrivalDaysAhead={arrivalDaysAhead}
          durationLabel={durationLabel}
          routeLabel={routeLabel}
          numberOfStops={numberOfStops}
          layoverDetail={layoverDetail}
        />
        {(priceDetails || actionCell) && (
          <CardFooter>
            {priceDetails}
            {actionCell}
          </CardFooter>
        )}
      </CardShell>
    </CardQueryContainer>
  )
}

export default memo(DrawerFlightCard)
